/**
 * When two chord spellings are the same keys. Validation, the store and the
 * Settings tab compare chords by these rules — never by spelling alone:
 *  - on one platform `Mod` is Cmd (macOS) or Ctrl (Windows/Linux), so
 *    `Mod+Q` and `Ctrl+Q` are the same chord off macOS;
 *  - a chord that cannot occur on the platform (`Cmd+…` off macOS) equals
 *    only its own spelling;
 *  - with a keyboard layout, a physical token equals the logical key the
 *    layout puts on it (`Ctrl+[Backquote]` and `Ctrl+\`` on a US layout).
 */
import { layoutTokenForCode, parseChord } from './chord';
import type { Chord, KeyboardLayoutSnapshot, Platform } from './keybindingTypes';

/** A chord's identity on one platform: `Mod` resolved, modifiers ordered; null when it cannot occur there. */
export function resolvedChordValue(chord: Chord, platform: Platform): string | null {
  if (platform !== 'mac' && chord.modifiers.includes('Cmd')) return null;
  const modifiers = new Set(chord.modifiers.map((modifier) => modifier === 'Mod'
    ? (platform === 'mac' ? 'Cmd' : 'Ctrl')
    : modifier));
  return `${[...modifiers].sort().join('+')}|${chord.physical ? 'physical' : 'logical'}:${chord.key}`;
}

/** Two chord spellings that are the same keys on `platform` (`Mod+Q` and `Ctrl+Q` off macOS). */
export function sameResolvedChord(left: string, right: string, platform: Platform): boolean {
  if (left === right) return true;
  const a = parseChord(left);
  const b = parseChord(right);
  if (!a || !b) return false;
  const identity = resolvedChordValue(a, platform);
  return identity !== null && identity === resolvedChordValue(b, platform);
}

/**
 * `sameResolvedChord`, plus a physical token equal to the logical key the
 * current layout maps it to (same resolved modifiers). Without a ready layout
 * the two stay distinct.
 */
export function sameChordOnLayout(
  left: string,
  right: string,
  platform: Platform,
  layout: KeyboardLayoutSnapshot,
): boolean {
  if (sameResolvedChord(left, right, platform)) return true;
  const a = parseChord(left);
  const b = parseChord(right);
  if (!a || !b || a.physical === b.physical) return false;
  const [physical, logical] = a.physical ? [a, b] : [b, a];
  const mapped = layoutTokenForCode(physical.key, layout);
  if (mapped === null || mapped !== logical.key) return false;
  const identity = resolvedChordValue({ modifiers: physical.modifiers, key: mapped, physical: false }, platform);
  return identity !== null && identity === resolvedChordValue(logical, platform);
}
